/**
 * datev-ai.adapter.ts
 *
 * Adapter that converts raw DATEV Excel data (ISpreadSheet) into the
 * AumicoNode tree format used by the AI agent.
 *
 * FORWARD:  ISpreadSheet → AumicoSections → AiAgentJobRequest
 *   Delegates to DatevExcelHelper.extractAumicoNodeSections(), which handles
 *   both DATEV-format and aumico-format files and produces AumicoNode[] trees.
 *
 * BACKWARD: AI output (AumicoSections) → TaxonomyDefinitions
 *   buildTaxonomyFromFixedSections() extracts SpecialFLIs from the original
 *   spreadsheet and calls DatevExcelHelper.convertAumicoSectionsToTaxonomy()
 *   to rebuild the full IAccountMappingTemplateGroup tree, producing a
 *   TaxonomyDefinitions that is identical to a successful direct import.
 */
import { Injectable } from '@angular/core';

import { AumicoNode, TaxonomyDefinitions } from './datev.excel.helper';
import { DatevExcelHelper } from './datev.excel.helper';
import { AiAgentJobRequest } from '@modeso/aumico-lib-ai-agents-fe';
import { ISpreadSheet } from '../interfaces/ISpreadSheet';

/** The three taxonomy sections sent to / received from the AI backend. */
export type AumicoSections = {
    'Bilanz-Aktiva': AumicoNode[];
    'Bilanz-Passiva': AumicoNode[];
    'GuV': AumicoNode[];
};

@Injectable()
export class DatevAiAdapter {

    constructor(private readonly datevExcelHelper: DatevExcelHelper) {}

    // ═══════════════════════════════════════════════════════════════════
    //  FORWARD: Raw Excel → AI job request
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Build an AiAgentJobRequest directly from the raw DATEV/aumico spreadsheet.
     *
     * Delegates format detection and node extraction to
     * DatevExcelHelper.extractAumicoNodeSections(), producing the same
     * AumicoNode[] structure for both DATEV and aumico file formats.
     */
    buildJobRequestFromSpreadSheet(
        spreadSheetData: ISpreadSheet,
        annualReportId?: string,
    ): AiAgentJobRequest {
        const sections: AumicoSections =
            this.datevExcelHelper.extractAumicoNodeSections(spreadSheetData);

        // Normalize levels per section so that the minimum level becomes 1
        for (const key of Object.keys(sections) as (keyof AumicoSections)[]) {
            normalizeLevels(sections[key]);
        }

        return {
            taskType: 'DATEV_FIX',
            payload: {
                sections,
                ...(annualReportId ? { annualReportId } : {}),
            },
        };
    }

    // ═══════════════════════════════════════════════════════════════════
    //  BACKWARD: AI output → TaxonomyDefinitions (continue normal flow)
    // ═══════════════════════════════════════════════════════════════════

    /** Extract the fixed AumicoSections from the AI session output. */
    extractFixedSections(output: Record<string, unknown>): AumicoSections {
        return output['sections'] as AumicoSections;
    }

    /**
     * Convert AI-fixed sections back into a TaxonomyDefinitions — the same
     * object shape produced by a clean DATEV file import.
     *
     * Uses the original spreadsheet to recover SpecialFLIs (annual surplus,
     * profit-of-the-year, equity group names etc.) from the template-info sheet,
     * then delegates the tree conversion to DatevExcelHelper.
     *
     * Returns null if the spreadsheet is missing its template-info sheet.
     */
    buildTaxonomyFromFixedSections(
        originalSpreadSheet: ISpreadSheet,
        fixedSections: AumicoSections,
    ): TaxonomyDefinitions | null {
        const specialFlis = this.datevExcelHelper.extractSpecialFlisFromSpreadSheet(originalSpreadSheet);
        if (!specialFlis) return null;

        return this.datevExcelHelper.convertAumicoSectionsToTaxonomy(fixedSections, specialFlis);
    }
}
function findMinLevel(nodes: AumicoNode[]): number {
    let min = Infinity;
    for (const node of nodes) {
        if (node.level < min) min = node.level;
        if (node.children?.length) {
            const childMin = findMinLevel(node.children);
            if (childMin < min) min = childMin;
        }
    }
    return min;
}

function shiftLevels(nodes: AumicoNode[], delta: number): void {
    for (const node of nodes) {
        node.level -= delta;
        if (node.children?.length) {
            shiftLevels(node.children, delta);
        }
    }
}


function normalizeLevels(nodes: AumicoNode[]): void {
    if (!nodes.length) return;
    const min = findMinLevel(nodes);
    if (min > 1) {
        shiftLevels(nodes, min - 1);
    }
}
